// consumption-chart.ts
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration } from 'chart.js';
import { ChartHeader } from './chart-header/chart-header';
import { ConsumptionData } from '../consumption-table/consumption-table';
import { MatTableDataSource } from '@angular/material/table';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-consumption-chart',
  standalone: true,
  imports: [BaseChartDirective, ChartHeader],
  templateUrl: './consumption-chart.html',
  styleUrl: './consumption-chart.scss'
})
export class ConsumptionChart {

  dataSource = new MatTableDataSource<ConsumptionData>([]);
  private chartRows: ConsumptionData[] = [];
  private chartDateFormat = 'dd.MM.yyyy';
  totalConsumption: number | null = null;

  // zentrale Fallbacks (deine bisherigen Farben)
  private readonly fallback = {
    consumption: {line: '#2196f3', bg: 'rgba(33,150,243,0.3)', point: '#2196f3'},
    prevYear: {line: '#9e9e9e'},
    // NEW: Außentemperatur-Farben
    outdoor: { line: '#4caf50', bg: 'rgba(76,175,80,0.15)', point: '#4caf50' }
  };

  @Input() set data(value: ConsumptionData[] | null | undefined) {
    const rows = (value ?? []).slice();

    // sortieren (aufsteigend)
    rows.sort((a, b) => {
      const ta = a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime();
      const tb = b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime();
      return ta - tb;
    });

    this.dataSource.data = rows;
    this.chartRows = rows;

    // Gesamtverbrauch: letzter Zählerstand minus erster Zählerstand
    const firstMeter = rows.find(r => typeof r.meter_amount === 'number')?.meter_amount ?? null;
    const lastMeter = [...rows].reverse().find(r => typeof r.meter_amount === 'number')?.meter_amount ?? null;
    this.totalConsumption = (firstMeter !== null && lastMeter !== null && lastMeter >= firstMeter)
      ? lastMeter - firstMeter
      : null;

    // Label-Format …
    let dateFormat = 'dd.MM.yyyy';
    if (rows.length > 1) {
      const first = rows[0].date instanceof Date ? rows[0].date : new Date(rows[0].date);
      const second = rows[1].date instanceof Date ? rows[1].date : new Date(rows[1].date);
      const diffMs = second.getTime() - first.getTime();
      const oneDayMs = 24 * 60 * 60 * 1000;
      if (diffMs < oneDayMs) dateFormat = 'dd.MM.yyyy HH:mm';
    }

    this.chartDateFormat = dateFormat;
    const labels = rows.map(r => formatDate(r.date, dateFormat, 'de-DE'));

    // Serien
    const consumptionSeries = this.calculateIntervalConsumption(rows);
    const prevYearSeries = rows.map(r => typeof r.prev_year_consumption === 'number' ? r.prev_year_consumption : null);
    const hasPrevYear = prevYearSeries.some(v => v !== null);
    // --- Farben aus API holen und NORMALISIEREN ---
    const apiConsumptionColor = this.firstColor(rows, 'consumption_line_color');

    // normalize -> dann Fallback
    const consLine = this.resolveColor(apiConsumptionColor, this.fallback.consumption.line);
    const consBg   = this.withAlpha(consLine, 0.3) ?? this.fallback.consumption.bg;
    const consPoint= consLine;

    const outdoorSeries = rows.map(r => typeof r.outside_temperature === 'number' ? r.outside_temperature : null);
    const hasOutdoor = outdoorSeries.some(v => v !== null);

    const apiOutdoorColor = this.firstColor(rows, 'outside_line_color');
    const outdoorLine = this.resolveColor(apiOutdoorColor, this.fallback.outdoor.line);
    const outdoorBg   = this.withAlpha(outdoorLine, 0.15) ?? this.fallback.outdoor.bg;
    const outdoorPoint= outdoorLine;

    const datasets: ChartConfiguration<'line'>['data']['datasets'] = [
      {
        ...this.baseConsumptionDataset,
        data: consumptionSeries,
        yAxisID: 'y',
        borderColor: consLine,
        backgroundColor: consBg,
        pointBackgroundColor: consPoint,
      },
      ...(hasPrevYear ? [{
        ...this.basePrevYearDataset,
        data: prevYearSeries,
        yAxisID: 'y',
        borderColor: this.fallback.prevYear.line,
      }] : [])
    ];

    if (hasOutdoor) {
      datasets.push({
        ...this.baseOutdoorTemperatureDataset,
        data: outdoorSeries,
        yAxisID: 'y1',
        borderColor: outdoorLine,
        backgroundColor: outdoorBg,
        pointBackgroundColor: outdoorPoint,
      });
    }

    this.lineChartData = { labels, datasets };
  }

  @Output() timeRangeChange = new EventEmitter<{ start: Date; end: Date }>();

  onTimeRangeChanged(event: { start: Date; end: Date }) {
    this.timeRangeChange.emit(event);
  }

  public lineChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [],
  };

  // Basis-Styles OHNE feste Farben
  private baseConsumptionDataset: ChartConfiguration<'line'>['data']['datasets'][number] = {
    data: [],
    label: 'Verbrauch (kWh)',
    fill: false,
    tension: 0.2,
    pointRadius: 3,
    spanGaps: true,
  };

  private basePrevYearDataset: ChartConfiguration<'line'>['data']['datasets'][number] = {
    data: [],
    label: 'Vorjahr (kWh)',
    borderDash: [6, 4],
    fill: false,
    tension: 0.2,
    pointRadius: 0,
    pointHitRadius: 6,
    spanGaps: true,
  };

  // Basis-Dataset für Außentemperatur
  private baseOutdoorTemperatureDataset: ChartConfiguration<'line'>['data']['datasets'][number] = {
    data: [],
    label: 'Außentemperatur Mittel (°C)',
    fill: false,
    tension: 0.2,
    pointRadius: 2,
    spanGaps: true,
  };

  public lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    plugins: {
      legend: {display: true},
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset?.label ?? '';
            const y = ctx.parsed.y;
            if (label.includes('Vorjahr')) {
              const row = this.chartRows[ctx.dataIndex];
              if (row) {
                const d = row.date instanceof Date ? row.date : new Date(row.date);
                const prevDate = new Date(d);
                prevDate.setFullYear(prevDate.getFullYear() - 1);
                const prevDateStr = formatDate(prevDate, this.chartDateFormat, 'de-DE');
                return ` ${y} kWh (${prevDateStr})`;
              }
              return ` ${y} kWh`;
            }
            if (label.includes('kWh')) return ` ${y} kWh`;
            if (label.includes('°C')) return ` ${y} °C`;
            return ` ${y}`;
          }
        }
      }
    },
    scales: {
      y: {beginAtZero: true, title: {display: true, text: 'kWh'}},
      y1: {position: 'right', grid: {drawOnChartArea: false}, title: {display: true, text: '°C'}},
      x: {ticks: {maxRotation: 45, minRotation: 45}}
    },
  };

  // 1) Vorwärts-Differenzen berechnen (letzter Punkt = null)
  private calculateIntervalConsumption(rows: ConsumptionData[]): (number | null)[] {
    const result: (number | null)[] = [];
    for (let i = 0; i < rows.length; i++) {
      const curr = rows[i]?.meter_amount;
      const next = rows[i + 1]?.meter_amount;
      if (i === rows.length - 1) {
        result.push(null);
        continue;
      }
      if (typeof curr === 'number' && typeof next === 'number') {
        const diff = next - curr;
        result.push(diff >= 0 ? diff : null);
      } else {
        result.push(null);
      }
    }
    return result;
  }

  /** erste gesetzte Farbe aus den Rows für den Key */
  private firstColor(
    rows: ConsumptionData[],
    key: 'consumption_line_color' | 'flow_line_color' | 'meter_line_color' | 'outside_line_color'
  ): string | undefined {
    for (const r of rows) {
      const c = (r as any)?.[key] as string | undefined;
      if (c && typeof c === 'string' && c.trim()) return c.trim();
    }
    return undefined;
  }

  /** Normalisiert API-Farben in gültige CSS-Farben (z. B. "FF4500" → "#FF4500"). */
  private normalizeCssColor(color?: string): string {
    if (!color) return '';
    const c = color.trim();

    // Bereits gültig (beginnt mit '#', 'rgb', 'rgba', 'hsl', 'hsla', 'var(') -> durchlassen
    if (/^(#|rgb\(|rgba\(|hsl\(|hsla\(|var\()/.test(c)) return c;

    // 0xAARRGGBB / 0xRRGGBB
    const m0x = c.match(/^0x([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
    if (m0x) return '#' + m0x[1];

    // Reines Hex ohne Prefix: 3/4/6/8 Stellen
    const mHex = c.match(/^([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
    if (mHex) return '#' + mHex[1];

    // Unbekanntes Format: zurückgeben (Chart.js versucht es), ggf. Fallback greift später
    return c;
  }

  /** Liefert normalisierte Farbe oder Fallback */
  private resolveColor(color: string | undefined, fallback: string): string {
    const n = this.normalizeCssColor(color);
    return n && n.trim() ? n : fallback;
  }

  /** Baut rgba aus #RGB/#RRGGBB/#RRGGBBAA oder rgb/rgba; sonst undefined */
  private withAlpha(color: string, alpha: number): string | undefined {
    if (!color) return undefined;
    const c = color.trim();

    if (c.startsWith('#')) {
      const hex = c.slice(1);
      let r = 0, g = 0, b = 0, a: number | null = null;
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6 || hex.length === 8) {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
        if (hex.length === 8) a = parseInt(hex.slice(6, 8), 16) / 255;
      } else {
        return undefined;
      }
      const baseA = a ?? 1;
      const finalA = Math.max(0, Math.min(1, alpha * baseA));
      return `rgba(${r}, ${g}, ${b}, ${finalA})`;
    }

    const m = c.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9.]+))?\s*\)$/i);
    if (m) {
      const r = +m[1], g = +m[2], b = +m[3];
      const baseA = m[4] != null ? Math.max(0, Math.min(1, +m[4])) : 1;
      const finalA = Math.max(0, Math.min(1, alpha * baseA));
      return `rgba(${r}, ${g}, ${b}, ${finalA})`;
    }
    return undefined;
  }

  public exportCsv(): void {
    const rows = this.dataSource?.data ?? [];
    const consumptionSeries = this.calculateIntervalConsumption(rows);

    // Kopfzeile
    const header = [
      'Datum',
      'Zählerstand (kWh)',
      'Verbrauch (kWh)',
      'Vorjahr (kWh)'
    ];

    const lines: string[] = [];
    lines.push(header.join(';'));

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];

      // Datum formatiert wie in der Tabelle
      const dateStr = formatDate(
        r.date instanceof Date ? r.date : new Date(r.date),
        'dd.MM.yyyy HH:mm',
        'de-DE'
      );

      // Verbrauch nach Chart-Logik (Vorwärts-Differenz), letzter Punkt = null
      const cons = consumptionSeries[i];

      // Vorjahr: solange keine echten Daten -> 0 wenn Verbrauch vorhanden, sonst leer
      const prevYear = cons != null ? (r as any).prev_year_consumption ?? 0 : null;

      const row = [
        this.csvEscape(dateStr),
        this.csvNumber(r.meter_amount),
        this.csvNumber(cons),
        this.csvNumber(prevYear),
      ].join(';');

      lines.push(row);
    }

    // BOM für Excel, CRLF für bessere Kompatibilität
    const csvContent = '\uFEFF' + lines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `verbrauch_export_${formatDate(new Date(), 'yyyy-MM-dd_HH-mm', 'de-DE')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** Formatiert Zahlen wie in der UI (1 Nachkommastelle). Null/undefined -> leeres Feld. */
  private csvNumber(v: number | null | undefined): string {
    if (typeof v !== 'number' || !isFinite(v)) return '';
    // Für Excel-DE: Dezimal-Komma verwenden
    return (Math.round((v + Number.EPSILON) * 10) / 10).toFixed(1).replace('.', ',');
  }

  /** CSV-escaping für Felder mit ;, " oder Zeilenumbrüchen. */
  private csvEscape(s: string): string {
    if (s == null) return '';
    const needsQuote = /[;"\r\n]/.test(s);
    let t = s.replace(/"/g, '""');
    return needsQuote ? `"${t}"` : t;
  }
}
